import React, { useEffect, useMemo, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { cachedFetch } from "../api.js";
import { Badge } from "../components/Badge.jsx";
import { Spinner } from "../components/Spinner.jsx";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

// Read pagination state from the current URL's query string. Any invalid
// value falls back to defaults — this is a user-facing surface, so pasted
// URLs with garbage (?page=abc, ?size=999) should degrade gracefully
// rather than throwing or rendering broken state.
function readPageStateFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const rawPage = parseInt(params.get("page"), 10);
  const rawSize = parseInt(params.get("size"), 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;
  const pageSize = PAGE_SIZE_OPTIONS.includes(rawSize) ? rawSize : DEFAULT_PAGE_SIZE;
  return { page, pageSize };
}

// Serialize pagination state into the URL without adding a history entry.
// - replaceState (not pushState) so the back button doesn't walk through
//   every page-nav click — much less surprising for a user paging
//   through a long matter list.
// - Default values omitted from the query string so "clean" URLs stay
//   clean; only diverging state appears in the URL.
// - Hash preserved so app routing (#matters etc.) survives untouched.
function writePageStateToUrl(page, pageSize) {
  const params = new URLSearchParams(window.location.search);
  if (page === 1) params.delete("page");
  else params.set("page", String(page));
  if (pageSize === DEFAULT_PAGE_SIZE) params.delete("size");
  else params.set("size", String(pageSize));
  const qs = params.toString();
  const url = qs
    ? `${window.location.pathname}?${qs}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`;
  window.history.replaceState(null, "", url);
}

export function MattersPage({ onMatterClick }) {
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  // Hydrate page + pageSize from URL on mount so refresh, back-forward
  // navigation, and pasted links all restore the user's position.
  const [{ page, pageSize }, setPageState] = useState(readPageStateFromUrl);
  const setPage = (updater) => setPageState(s => ({
    ...s,
    page: typeof updater === "function" ? updater(s.page) : updater,
  }));
  const setPageSize = (n) => setPageState(s => ({ ...s, pageSize: n }));

  useEffect(() => {
    cachedFetch("/api/matters")
      .then(data => { setMatters(data.data || []); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  // Any change to search or page-size resets to page 1.
  // Without this: type a search, currently on page 3, results shrink
  // to 4 items, page 3 shows nothing.
  useEffect(() => { setPage(1); }, [searchQuery, pageSize]);

  // Mirror page + pageSize into the URL query string.
  useEffect(() => { writePageStateToUrl(page, pageSize); }, [page, pageSize]);

  const filtered = useMemo(() => matters.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const name = (m.client?.name || "").toLowerCase();
    const num = (m.display_number || "").toLowerCase();
    return name.includes(q) || num.includes(q);
  }), [matters, searchQuery]);

  // Derived pagination state
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // Clamp page in case filtered set shrinks (e.g. search filters below page*pageSize)
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paged = filtered.slice(startIdx, endIdx);
  const showPagination = filtered.length > pageSize;

  return React.createElement("div", { className:"fade-in", style: { padding:"32px 40px", maxWidth:"1100px" } },
    React.createElement("h1", {
      style: { fontFamily:"'Playfair Display',serif", fontSize:"28px", fontWeight:600, color:C.navy, marginBottom:"24px" }
    }, "Matters"),

    // Search
    React.createElement("div", { style: { position:"relative", marginBottom:"20px" } },
      React.createElement("span", {
        style: { position:"absolute", left:"14px", top:"50%", transform:"translateY(-50%)", color:C.textLight }
      }, I.search),
      React.createElement("input", {
        type: "text", placeholder: "Search by client name or matter number...",
        value: searchQuery, onChange: e => setSearchQuery(e.target.value),
        style: {
          width:"100%", padding:"12px 14px 12px 40px", borderRadius:"10px",
          border:`1px solid ${C.warmGray}`, fontSize:"14px", background:"#fff", color:C.textDark,
        },
      }),
    ),

    // Error
    error && React.createElement("div", {
      style: { padding:"16px 20px", borderRadius:"10px", background:C.errorBg, color:C.error, fontSize:"14px", marginBottom:"16px" }
    }, "Error loading matters: ", error),

    // Table
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", border:`1px solid ${C.warmGray}`, overflow:"hidden" }
    },
      // Header Row.
      // `list-head` lets the stylesheet hide it on a phone — column
      // headings for a layout that is no longer columns are just four
      // stacked words. See responsive.css.
      React.createElement("div", {
        className: "list-head",
        style: {
          display:"grid", gridTemplateColumns:"1.5fr 1fr 0.8fr 40px",
          padding:"10px 20px", borderBottom:`1px solid ${C.warmGray}`, background:C.warmWhite,
        }
      },
        ["Client", "Matter #", "Status", ""].map((h, i) =>
          React.createElement("div", {
            key: i,
            style: { fontSize:"11px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:600 }
          }, h)
        )
      ),

      loading
        ? React.createElement("div", { style: { padding:"40px", textAlign:"center" } }, React.createElement(Spinner, null))
        : filtered.length === 0
          ? React.createElement("div", { style: { padding:"40px", textAlign:"center", color:C.textMuted } }, "No matters found.")
          : paged.map((m, i) =>
              React.createElement("button", {
                key: m.id,
                onClick: () => onMatterClick(m),
                // `list-row` reflows this into a two-line card on a
                // phone: client name across the top, matter number and
                // status beneath, chevron holding the right edge.
                className: "list-row",
                style: {
                  width:"100%", display:"grid", gridTemplateColumns:"1.5fr 1fr 0.8fr 40px",
                  padding:"14px 20px", border:"none", background:"transparent",
                  cursor:"pointer", textAlign:"left", alignItems:"center",
                  borderBottom: i < paged.length - 1 ? `1px solid ${C.warmGray}40` : "none",
                  transition:"background 0.15s",
                },
                onMouseEnter: e => { e.currentTarget.style.background = C.warmWhite; },
                onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
              },
                React.createElement("div", null,
                  React.createElement("span", { style: { fontSize:"14px", fontWeight:600, color:C.navy } },
                    m.client?.name || `Matter #${m.id}`
                  ),
                ),
                React.createElement("div", { style: { fontSize:"13px", color:C.textMuted } }, m.display_number || ""),
                React.createElement("div", null, React.createElement(Badge, { variant:"gold" }, m.status || "Open")),
                React.createElement("div", { style: { color:C.textLight, display:"flex", justifyContent:"flex-end" } }, I.chevron),
              )
            ),
    ),

    // Pagination controls — only rendered when there's more than one page.
    // Layout: left = "showing X-Y of Z", right = page-size + prev/page/next.
    // Hidden entirely when everything fits on one page to reduce clutter
    // for firms with small matter counts.
    !loading && showPagination && React.createElement("div", {
      style: {
        display:"flex", justifyContent:"space-between", alignItems:"center",
        marginTop:"16px", padding:"12px 4px", fontSize:"13px", color:C.textMuted,
        gap:"12px", flexWrap:"wrap",
      }
    },
      // Left: count summary
      React.createElement("div", null,
        `Showing ${startIdx + 1}–${Math.min(endIdx, filtered.length)} of ${filtered.length}`,
        searchQuery && ` (filtered from ${matters.length})`,
      ),

      // Right: page-size selector + prev/next
      React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"12px" } },
        React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"6px" } },
          React.createElement("label", {
            htmlFor: "matters-page-size", style: { fontSize:"12px" }
          }, "Per page:"),
          React.createElement("select", {
            id: "matters-page-size",
            value: pageSize,
            onChange: e => setPageSize(Number(e.target.value)),
            style: {
              padding:"4px 8px", borderRadius:"6px",
              border:`1px solid ${C.warmGray}`, background:"#fff",
              color:C.textDark, fontSize:"13px", cursor:"pointer",
            }
          }, PAGE_SIZE_OPTIONS.map(n =>
            React.createElement("option", { key: n, value: n }, n)
          )),
        ),
        React.createElement("button", {
          onClick: () => setPage(p => Math.max(1, p - 1)),
          disabled: currentPage <= 1,
          style: {
            padding:"6px 12px", borderRadius:"6px",
            border:`1px solid ${C.warmGray}`,
            background: currentPage <= 1 ? C.warmWhite : "#fff",
            color: currentPage <= 1 ? C.textLight : C.textDark,
            fontSize:"13px", fontWeight:500,
            cursor: currentPage <= 1 ? "not-allowed" : "pointer",
          }
        }, "‹ Prev"),
        React.createElement("span", {
          style: { fontSize:"13px", fontWeight:500, color:C.textDark, minWidth:"70px", textAlign:"center" }
        }, `Page ${currentPage} of ${totalPages}`),
        React.createElement("button", {
          onClick: () => setPage(p => Math.min(totalPages, p + 1)),
          disabled: currentPage >= totalPages,
          style: {
            padding:"6px 12px", borderRadius:"6px",
            border:`1px solid ${C.warmGray}`,
            background: currentPage >= totalPages ? C.warmWhite : "#fff",
            color: currentPage >= totalPages ? C.textLight : C.textDark,
            fontSize:"13px", fontWeight:500,
            cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
          }
        }, "Next ›"),
      ),
    ),
  );
}

